import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../utils/api';
import { fmt, fmtDate, fmtTime } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import Pager from '../components/Pager';
import InvoiceViewModal from '../components/InvoiceViewModal';
import Alert from '../components/Alert';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewInvoiceId, setViewInvoiceId] = useState(null);

  // Low stock separate state
  const [lowStock, setLowStock] = useState({ products: [], total: 0, page: 1, pages: 1, threshold: 50 });
  const [lowStockPage, setLowStockPage] = useState(1);
  const [lowStockLoading, setLowStockLoading] = useState(false);

  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api.get('/dashboard')
      .then(res => { if (mounted) setData(res.data); })
      .catch(() => setError('Could not load dashboard data.'))
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    setLowStockLoading(true);
    api.get('/dashboard/low-stock', { params: { page: lowStockPage } })
      .then(res => setLowStock(res.data))
      .catch(() => {})
      .finally(() => setLowStockLoading(false));
  }, [isAdmin, lowStockPage]);

  return (
    <Layout title="Dashboard">
      <Alert type="danger" message={error} onClose={() => setError('')} />
      {loading ? (
        <div className="empty-state">Loading dashboard...</div>
      ) : (
        data && (
          <>
            <div className="metric-grid">
              <div className="metric">
                <div className="metric-label">Total Products</div>
                <div className="metric-value">{data.totalProducts}</div>
                <div className="metric-sub">in catalogue</div>
              </div>
              <div className="metric blue">
                <div className="metric-label">Total Invoices</div>
                <div className="metric-value">{data.totalInvoices}</div>
                <div className="metric-sub">all time</div>
              </div>
              <div className="metric orange">
                <div className="metric-label">Total Revenue</div>
                <div className="metric-value" style={{ fontSize: 16 }}>
                  ₹{Number(data.totalRevenue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </div>
                <div className="metric-sub">grand total</div>
              </div>
              <div className="metric red">
                <div className="metric-label">Low Stock Items</div>
                <div className="metric-value">{data.lowStockCount}</div>
                <div className="metric-sub">below {data.lowStockThreshold} units</div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">Recent Invoices</div>
                <button className="btn btn-sm" onClick={() => navigate('/invoices')}>View all →</button>
              </div>
              {data.recentInvoices.length === 0 ? (
                <div className="empty-state">
                  <i className="ti ti-file-off" aria-hidden="true"></i>No invoices yet
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Customer</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentInvoices.map(i => (
                      <tr key={i.id}>
                        <td><span style={{ color: 'var(--green)', fontWeight: 600 }}>{i.invoice_number}</span></td>
                        <td>{i.customer_name || 'Walk-in'}</td>
                        <td>{fmtDate(i.invoice_date)}</td>
                        <td>{fmtTime(i.invoice_date)}</td>
                        <td><strong>{fmt(i.total)}</strong></td>
                        <td>
                          <button className="btn btn-sm" onClick={() => setViewInvoiceId(i.id)}>
                            <i className="ti ti-eye" aria-hidden="true"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {isAdmin && lowStock.total > 0 && (
              <div className="card" style={{ marginTop: '1rem' }}>
                <div className="card-header">
                  <div className="card-title" style={{ color: '#C62828' }}>
                    ⚠ Low Stock Alert
                  </div>
                  <span style={{ fontSize: 12, color: '#9e9e9e' }}>{lowStock.total} items below {lowStock.threshold} units</span>
                </div>
                {lowStockLoading ? (
                  <div className="empty-state" style={{ padding: '1rem' }}>Loading…</div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product</th>
                        <th>SKU</th>
                        <th>Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lowStock.products.map((p, idx) => (
                        <tr key={p.id}>
                          <td>{(lowStockPage - 1) * 10 + idx + 1}</td>
                          <td>{p.name}</td>
                          <td>{p.sku}</td>
                          <td>
                            <span className="badge badge-red">{p.qty}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <Pager
                  page={lowStockPage}
                  pages={lowStock.pages}
                  total={lowStock.total}
                  onPage={setLowStockPage}
                  label="items"
                />
              </div>
            )}
          </>
        )
      )}

      {viewInvoiceId && <InvoiceViewModal invoiceId={viewInvoiceId} onClose={() => setViewInvoiceId(null)} />}
    </Layout>
  );
}
